// src/Pages/Creator/EditCreator.js – Jul 2025 mobile‑ready rebuild (v2) with logout
// -----------------------------------------------------------------------------
//  ✦  New in this version
//  • Profile tab is now the default / left‑most tab, so users land there first.
//  • Added a simple toggle in the Profile tab that lets users declare whether
//    they are a "Creator" (default) or a "Contractor" (vendor on the backend).
//  • The selected account‑type is sent to the backend via `is_vendor` (boolean)
//    and `creator_type` ("vendor" | "creator").
//  • All previous mobile layout fixes (Save‑button offset, extra scroll space)
//    are preserved.
//  • Added logout functionality using useCreatorAuth hook
// -----------------------------------------------------------------------------

import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Button,
  Paper,
  Backdrop,
  CircularProgress,
  Tabs,
  Tab,
  Badge,
  List,
  ListItem,
  Alert,
  AlertTitle,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Box,
  Card,
  CardContent,
  Tooltip,
  Divider,
} from "@mui/material";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useGoogleLogin } from "@react-oauth/google";
import LogoutIcon from "@mui/icons-material/Logout";

import API from "../../../API";
import AlertDialog from "../../../Components/AlertDialog";
import useAlertDialog from "../../../Components/useAlertDialog";
import ConsentForm from "../popups/consentform";
import UploadFileDialog from "../../onboardupload";
import useIsDesktop from "../../../Hooks/useIsDesktop";
import { useCreatorAuth } from "../../../Hooks/creator-use-auth"; // Import creator auth hook
import routes from "../../../Config/routes";

import {
  platformSelection,
  validatePlatformSelection,
} from "../../../Components/CreatorData/creatorValidation";
import { parseLocalFloat, parseLocalInt } from "../../../Utils/constants";

/* redesigned tab components */
import PlatformsTab from "./usersettings/PlatformsTab";
import PartnerTab from "./usersettings/PaymentTab";
import ProfileTab from "./usersettings/ProfileTab";
import NotificationsTab from "./usersettings/notiftab";

/* ──────────────────────────────────────────────────────────────── */
/* CONSTANTS                                                       */
/* ──────────────────────────────────────────────────────────────── */
const PROMO_TYPES = [
  { key: "campaign_followup", label: "Campaign follow‑ups" },
  { key: "reminder", label: "General reminders" },
  { key: "special_offer", label: "Special offers" },
  { key: "event_notification", label: "Events & webinars" },
];

const NEWS_TYPES = [
  { key: "weekly_newsletter", label: "Weekly newsletter" },
  { key: "monthly_newsletter", label: "Monthly newsletter" },
];

// Height of the AppShell bottom‑nav bar (keep in sync with AppShell.js)
const MOBILE_BOTTOM_NAV_HEIGHT = 86; // px

/* ──────────────────────────────────────────────────────────────── */
/* MAIN COMPONENT                                                  */
/* ──────────────────────────────────────────────────────────────── */
export default function EditCreator() {
  /* URL / Layout ------------------------------------------------ */
  const [searchParams, setSearchParams] = useSearchParams();
  // Default to profile first
  const tabValue = searchParams.get("tab") || "profile";
  const referrer = searchParams.get("referrer");
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const { logout } = useCreatorAuth(); // Get creator logout function

  /* ─────────────── Logout handler ─────────────── */
  const handleLogout = () => {
    logout();
    navigate(routes.home || "/home");
  };

  /* Dialogs / loaders ------------------------------------------ */
  const { dialogState, openDialog, closeDialog } = useAlertDialog();
  const [isLoading, setIsLoading] = useState(false);
  const [showPlatformsTip, setShowPlatformsTip] = useState(false);

  /* Payment ----------------------------------------------------- */
  const [paymentMethod, setPaymentMethod] = useState("PayPal");
  const [isStripeNew, setIsStripeNew] = useState(true);
  const [stripeUserId, setStripeUserId] = useState("");
  const [processEmails, setProcessEmails] = useState(true);
  const [paymentEmail, setPaymentEmail] = useState("");

  /* Google / Partner flow -------------------------------------- */
  const [userStatus, setUserStatus] = useState("");
  const [googleRequest, setGoogleRequest] = useState("");
  const [openConsent, setOpenConsent] = useState(false);

  /* Basic creator info ----------------------------------------- */
  const [creatorName, setCreatorName] = useState("");
  const [creatorRegion, setCreatorRegion] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [race, setRace] = useState("");
  const [gender, setGender] = useState("");
  const [location, setLocation] = useState("");
  const [primaryMarket, setPrimaryMarket] = useState([]);
  const [notesContentStyle, setNotesContentStyle] = useState("");
  const [profilePhotoUrl, setProfilePhotoUrl] = useState("");

  /* NEW – account‑type toggle ---------------------------------- */
  const [isVendor, setIsVendor] = useState(false); // "Contractor" on UI

  /* Notification prefs ----------------------------------------- */
  const defaultPromo = PROMO_TYPES.map((i) => i.key);
  const defaultNews = NEWS_TYPES.map((i) => i.key);
  const [promo, setPromo] = useState(defaultPromo);
  const [news, setNews] = useState(defaultNews);
  const [frequency, setFrequency] = useState("weekly");

  /* Social / rates --------------------------------------------- */
  const [platforms, setPlatforms] = useState(platformSelection);
  const [instagramLink, setInstagramLink] = useState("");
  const [instagramFollowerCount, setInstagramFollowerCount] = useState("");
  const [instagramBrandRate, setInstagramBrandRate] = useState("");
  const [instagramSongRate, setInstagramSongRate] = useState("");
  const [instagramFeedRate, setInstagramFeedRate] = useState("");
  const [instagramStoryRate, setInstagramStoryRate] = useState("");
  const [tikTokLink, setTikTokLink] = useState("");
  const [tikTokFollowerCount, setTikTokFollowerCount] = useState("");
  const [tikTokBrandRate, setTikTokBrandRate] = useState("");
  const [tikTokSongRate, setTikTokSongRate] = useState("");
  const [youtubeLink, setYoutubeLink] = useState("");
  const [youtubeFollowerCount, setYoutubeFollowerCount] = useState("");
  const [youtubeIntegrationRate, setYoutubeIntegrationRate] = useState("");
  const [youtubeShortsRate, setYoutubeShortsRate] = useState("");
  const [youtubeDedicatedRate, setYoutubeDedicatedRate] = useState("");
  const [xLink, setXLink] = useState("");
  const [xFollowerCount, setXFollowerCount] = useState("");
  const [xFeedPost, setXFeedPost] = useState("");
  const [xRepost, setXRepost] = useState("");
  const [podcastLink, setPodcastLink] = useState("");
  const [podcastFollowerCount, setPodcastFollowerCount] = useState("");
  const [podcastPreroll, setPodcastPreroll] = useState("");
  const [podcastMidroll, setPodcastMidroll] = useState("");
  const [podcastPostroll, setPodcastPostroll] = useState("");
  const [podcastFull, setPodcastFull] = useState("");
  const [kickLink, setKickLink] = useState("");
  const [kickFollowerCount, setKickFollowerCount] = useState("");
  const [kickPlacement, setKickPlacement] = useState("");
  const [twitchLink, setTwitchLink] = useState("");
  const [twitchFollowerCount, setTwitchFollowerCount] = useState("");
  const [twitchPlacement, setTwitchPlacement] = useState("");

  /* Upload dialog ---------------------------------------------- */
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);

  const [forwardedAlias, setForwardedAlias] = useState("");

  /* Backend data holder ---------------------------------------- */
  const userData = useRef(null);

  /* Helper ------------------------------------------------------ */
  const formatNumber = (v) => {
    const n = parseInt(v ?? "", 10);
    return Number.isNaN(n) ? "" : n.toString();
  };

  /* Notification toggles --------------------------------------- */
  const toggleItem = useCallback(
    (setter) => (key) =>
      setter((prev) =>
        prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]),
    []
  );
  const toggleAllPromo = () =>
    setPromo(promo.length === defaultPromo.length ? [] : defaultPromo);
  const toggleAllNews = () =>
    setNews(news.length === defaultNews.length ? [] : defaultNews);

  /* ─────────────────────────  DATA FETCH  ────────────────────── */
  const GetUserData = async () => {
    try {
      setIsLoading(true);
      const [creatorRes, emailRes] = await Promise.all([
        API.creatorConnect.getCreator(),
        API.users.fetchEmailSettings(),
      ]);

      const user = creatorRes.user;
      userData.current = user;

      /* Basics */
      const cr = user.creator;
      setCreatorName(cr.display_name || "");
      setCreatorRegion(cr.region || "");
      setContactEmail(cr.email);
      setPhoneNumber(cr.phone_number || "1");
      setRace(cr.ethnicity || "");
      setGender(cr.gender || "");
      setLocation(cr.geolocation || "");
      setPrimaryMarket(cr.primary_markets || []);
      setNotesContentStyle(cr.notes_content_style || "");
      setProfilePhotoUrl(cr.pfphref || "");
      setForwardedAlias(user?.forwarded_alias ?? "");
      // NEW
      setIsVendor(!!cr.is_vendor);

      /* Status */
      setUserStatus(
        cr.status ? cr.status : user.refresh_token ? "Partner" : "Associate"
      );

      /* Payment */
      setPaymentEmail(user.paypal || "");
      setPaymentMethod(user.payout_preferred || "");
      setProcessEmails(user.process_emails);
      setIsStripeNew(!user.stripe_id);
      setStripeUserId(user.stripe_id || "");

      /* Platforms presence */
      setPlatforms({
        TikTok: !!cr.tiktok_link,
        Instagram: !!cr.instagram_link,
        YouTube: !!cr.youtube_link,
        X: !!cr.x_link,
        Podcast: !!cr.podcast_link,
        Kick: !!cr.kick_link,
        Twitch: !!cr.twitch_link,
      });

      /* Individual fields */
      setInstagramLink(
        cr.instagram_link?.replace("https://www.instagram.com/", "") || ""
      );
      setInstagramFollowerCount(formatNumber(cr.instagram));
      setInstagramBrandRate(cr.rate_ig_reelbrand?.toFixed(2) || "");
      setInstagramSongRate(cr.rate_ig_reelsound?.toFixed(2) || "");
      setInstagramFeedRate(cr.rate_ig_feedpost?.toFixed(2) || "");
      setInstagramStoryRate(cr.rate_ig_story?.toFixed(2) || "");

      setTikTokLink(
        cr.tiktok_link?.replace("https://www.tiktok.com/@", "") || ""
      );
      setTikTokFollowerCount(formatNumber(cr.tiktok));
      setTikTokBrandRate(cr.rate_tt_brand?.toFixed(2) || "");
      setTikTokSongRate(cr.rate_tt_sound?.toFixed(2) || "");

      setYoutubeLink(
        cr.youtube_link?.replace("https://www.youtube.com/", "") || ""
      );
      setYoutubeFollowerCount(formatNumber(cr.youtube));
      setYoutubeIntegrationRate(cr.rate_yt_integ?.toFixed(2) || "");
      setYoutubeShortsRate(cr.rate_yt_short?.toFixed(2) || "");
      setYoutubeDedicatedRate(cr.rate_yt_dedicated?.toFixed(2) || "");

      setXLink(cr.x_link?.replace("https://www.x.com/", "") || "");
      setXFollowerCount(formatNumber(cr.follow_x));
      setXFeedPost(cr.rate_x_feedpost?.toFixed(2) || "");
      setXRepost(cr.rate_x_repost?.toFixed(2) || "");

      setPodcastLink(cr.podcast_link || "");
      setPodcastFollowerCount(formatNumber(cr.follow_podcast));
      setPodcastPreroll(cr.rate_podcast_preroll?.toFixed(2) || "");
      setPodcastMidroll(cr.rate_podcast_midroll?.toFixed(2) || "");
      setPodcastPostroll(cr.rate_podcast_postroll?.toFixed(2) || "");
      setPodcastFull(cr.rate_podcast_full?.toFixed(2) || "");

      setKickLink(cr.kick_link?.replace("https://www.kick.com/", "") || "");
      setKickFollowerCount(formatNumber(cr.follow_kick));
      setKickPlacement(cr.rate_kick_product?.toFixed(2) || "");

      setTwitchLink(
        cr.twitch_link?.replace("https://www.twitch.tv/", "") || ""
      );
      setTwitchFollowerCount(formatNumber(cr.twitch));
      setTwitchPlacement(cr.rate_twitch_product?.toFixed(2) || "");

      /* Notifications */
      setPromo(
        emailRes.promotional_types?.length ? emailRes.promotional_types : defaultPromo
      );
      setNews(
        emailRes.newsletter_types?.length ? emailRes.newsletter_types : defaultNews
      );
      setFrequency(emailRes.email_frequency || "weekly");

      setIsLoading(false);
    } catch (err) {
      openDialog(
        "Error",
        err.response?.data?.error || err.message,
        closeDialog,
        closeDialog,
        "Ok",
        null
      );
      setIsLoading(false);
    }
  };
  useEffect(() => {
    GetUserData();
  }, []);

  /* ─────────────────────────  SAVE CREATOR  ───────────────────── */
  const buildPayload = (aliasOverride) => ({
    // socials & links -------------------------------------------------------
    tiktok: parseLocalInt(tikTokFollowerCount) || null,
    tiktok_link: tikTokLink ? `https://www.tiktok.com/@${tikTokLink}` : null,
    instagram: parseLocalInt(instagramFollowerCount) || null,
    instagram_link: instagramLink ? `https://www.instagram.com/${instagramLink}` : null,
    youtube: parseLocalInt(youtubeFollowerCount) || null,
    youtube_link: youtubeLink ? `https://www.youtube.com/${youtubeLink}` : null,
    twitch: parseLocalInt(twitchFollowerCount) || null,
    twitch_link: twitchLink ? `https://www.twitch.tv/${twitchLink}` : null,
    follow_x: parseLocalInt(xFollowerCount) || null,
    x_link: xLink ? `https://www.x.com/${xLink}` : null,
    follow_podcast: parseLocalInt(podcastFollowerCount) || null,
    podcast_link: podcastLink || null,
    follow_kick: parseLocalInt(kickFollowerCount) || null,
    kick_link: kickLink ? `https://www.kick.com/${kickLink}` : null,

    // rates -----------------------------------------------------------------
    rate_tt_sound: parseLocalFloat(tikTokSongRate),
    rate_tt_brand: parseLocalFloat(tikTokBrandRate),
    rate_ig_feedpost: parseLocalFloat(instagramFeedRate),
    rate_ig_reelsound: parseLocalFloat(instagramSongRate),
    rate_ig_reelbrand: parseLocalFloat(instagramBrandRate),
    rate_ig_story: parseLocalFloat(instagramStoryRate),
    rate_yt_integ: parseLocalFloat(youtubeIntegrationRate),
    rate_yt_short: parseLocalFloat(youtubeShortsRate),
    rate_yt_dedicated: parseLocalFloat(youtubeDedicatedRate),
    rate_podcast_preroll: parseLocalFloat(podcastPreroll),
    rate_podcast_midroll: parseLocalFloat(podcastMidroll),
    rate_podcast_postroll: parseLocalFloat(podcastPostroll),
    rate_podcast_full: parseLocalFloat(podcastFull),
    rate_twitch_product: parseLocalFloat(twitchPlacement),
    rate_kick_product: parseLocalFloat(kickPlacement),
    rate_x_feedpost: parseLocalFloat(xFeedPost),
    rate_x_repost: parseLocalFloat(xRepost),

    // info ------------------------------------------------------------------
    geolocation: location,
    gender,
    ethnicity: race,
    primary_markets: primaryMarket,
    region: creatorRegion,
    notescontent_style: notesContentStyle || null,
    contactEmail: contactEmail || null,
    paymentEmail: paymentEmail || null,
    phone_number: phoneNumber || null,
    payment_method: paymentMethod || null,
    stripe_id: stripeUserId,
    pfphref: profilePhotoUrl,
    status: userStatus,
    process_emails: processEmails,
    display_name: creatorName,

    // NEW account‑type -------------------------------------------------------
    is_vendor: isVendor,
    creator_type: isVendor ? "vendor" : "creator",

    // notifications ----------------------------------------------------------
    promotional_types: promo,
    newsletter_types: news,
    email_frequency: frequency,

    forwarded_alias: (aliasOverride ?? forwardedAlias) || null,
  });

  const handleSaveCreator = async (aliasOverride) => {
    try {
      setIsLoading(true);
      const payload = buildPayload(aliasOverride);

      const [{ request_id }] = await Promise.all([
        API.creatorConnect.editCreator(payload),
        API.users.updateEmailSettings({
          promotional_types: payload.promotional_types,
          newsletter_types: payload.newsletter_types,
          email_frequency: payload.email_frequency,
        }),
      ]);

      /* if just switched to Partner -> kick off OAuth */
      if (userStatus === "Partner" && !userData.current?.refresh_token) {
        setGoogleRequest(request_id);
        return; // OAuth redirect will happen
      }

      openDialog(
        "Success",
        "Creator data saved successfully.",
        closeDialog,
        closeDialog,
        "Ok",
        null
      );
      await GetUserData();
      setIsLoading(false);
    } catch (err) {
      openDialog(
        "Error",
        err.response?.data?.error || err.message,
        closeDialog,
        closeDialog,
        "Ok",
        null
      );
      setIsLoading(false);
    }
  };

  /* ─────────────────────────  HANDLERS  ───────────────────────── */
  const handlePaymentMethodChange = (e) => {
    setPaymentMethod(e.target.value);
    sessionStorage.setItem("paymentMethod", e.target.value);
    sessionStorage.setItem("creatorName", creatorName);
  };

  const handleChangeTab = (_, v) => setSearchParams((p) => ({ ...p, tab: v }));

  /* credential validations for badges --------------------------- */
  const anyPlatformSelected = Object.values(platforms).some(Boolean);
  const errorPlatform = anyPlatformSelected ? 0 : 1;
  const errorInfo =
    !creatorName ||
    !contactEmail ||
    phoneNumber.length < 11 ||
    !creatorRegion ||
    !race ||
    !gender ||
    !location ||
    primaryMarket.length === 0 ||
    !notesContentStyle ||
    (paymentMethod === "PayPal" && !paymentEmail)
      ? 1
      : 0;

  /* highlight query param ("?high=platforms") -------------------- */
  const high = searchParams.get("high");
  useEffect(() => {
    if (high === "platforms") {
      setShowPlatformsTip(true);
      setSearchParams((p) => ({ ...p, tab: "platforms" }));
      const t = setTimeout(() => setShowPlatformsTip(false), 6000);
      return () => clearTimeout(t);
    }
  }, [high]);

  /* auto‑set Partner if ?referrer=... ---------------------------- */
  useEffect(() => {
    if (referrer) setUserStatus("Partner");
  }, [referrer]);

  /* ────────────────────────────  RENDER  ──────────────────────── */
  return (
    <>
      {/* global loaders & dialogs */}
      <Backdrop
        sx={{ color: "#fff", zIndex: (t) => t.zIndex.drawer - 1 }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <AlertDialog alertState={dialogState} />
      <ConsentForm
        open={openConsent}
        onClose={(consented) => {
          setOpenConsent(false);
          if (consented) {
            /* beginGoogleConnect(); // (kept for future – currently removed) */
          }
        }}
      />

      {/* NEW: Header card with creator info and logout */}
      <Card
        sx={{
          background: "linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)",
          color: "white",
          mb: 3,
          borderRadius: 3,
          boxShadow: 3,
        }}
      >
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                  Creator Settings
                </Typography>
                <Typography variant="subtitle1">
                  {creatorName || "Update your profile"}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Account Type: {isVendor ? "Contractor" : "Creator"}
                </Typography>
              </Box>
            </Box>
            <Tooltip title="Log out">
              <Button
                variant="outlined"
                startIcon={<LogoutIcon />}
                onClick={handleLogout}
                sx={{
                  color: "white",
                  borderColor: "rgba(255,255,255,0.5)",
                  "&:hover": {
                    borderColor: "white",
                    backgroundColor: "rgba(255,255,255,0.1)",
                  },
                }}
              >
                Logout
              </Button>
            </Tooltip>
          </Box>
        </CardContent>
      </Card>

      {/* referral banner */}
      {referrer && (
        <Alert severity="info" sx={{ my: 2 }}>
          You were referred to join our Partner Program. Review your details and
          click <strong>Save Changes</strong>, then connect Google to activate
          full Partner benefits.
        </Alert>
      )}

      {/* primary nav tabs (Profile first) */}
      <Paper elevation={4} square>
        <Tabs centered={isDesktop} value={tabValue} onChange={handleChangeTab}>
          <Tab
            value="profile"
            label={<Badge variant="dot" color="error" badgeContent={errorInfo}>Profile</Badge>}
          />
          <Tab
            value="platforms"
            label={<Badge variant="dot" color="error" badgeContent={errorPlatform}>Platforms</Badge>}
          />
          <Tab value="partner" label="Partner" />
          <Tab value="notifications" label="Notifications" />
        </Tabs>
      </Paper>

      {/* warning if no platform */}
      {!anyPlatformSelected && (
        <Alert variant="filled" severity="warning" sx={{ my: 4 }}>
          <AlertTitle>Incomplete Platform Information</AlertTitle>
          <List sx={{ listStyleType: "disc", pl: 4 }}>
            <ListItem disablePadding sx={{ display: "list-item" }}>
              Select at least one platform and add a rate.
            </ListItem>
          </List>
        </Alert>
      )}

      {/* form section */}
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          handleSaveCreator();
        }}
      >
        <Paper
          elevation={1}
          sx={{
            px: 4,
            pt: 2,
            pb: { xs: 20, md: 10 }, // extra bottom padding for mobile scroll space
            mt: 3,
            maxWidth: "50em",
            mx: "auto",
          }}
        >
          {/* Profile‑first */}
          <ProfileTab
            tabValue={tabValue}
            index="profile"
            creatorName={creatorName}
            setCreatorName={setCreatorName}
            profilePhotoUrl={profilePhotoUrl}
            setIsUploadDialogOpen={setIsUploadDialogOpen}
            creatorRegion={creatorRegion}
            setCreatorRegion={setCreatorRegion}
            race={race}
            setRace={setRace}
            gender={gender}
            setGender={setGender}
            location={location}
            setLocation={setLocation}
            primaryMarket={primaryMarket}
            setPrimaryMarket={setPrimaryMarket}
            notesContentStyle={notesContentStyle}
            setNotesContentStyle={setNotesContentStyle}
            contactEmail={contactEmail}
            setContactEmail={setContactEmail}
            phoneNumber={phoneNumber}
            setPhoneNumber={setPhoneNumber}
            paymentMethod={paymentMethod}
            handlePaymentMethodChange={handlePaymentMethodChange}
            paymentEmail={paymentEmail}
            setPaymentEmail={setPaymentEmail}  // ADD THIS LINE
          />

          {/* NEW – account type toggle, only visible inside Profile tab */}
          {tabValue === "profile" && (
            <Box sx={{ mt: 4 }}>
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" gutterBottom>
                Account Type
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Choose whether you're primarily a content creator or a contractor/vendor
              </Typography>
              <ToggleButtonGroup
                exclusive
                value={isVendor ? "contractor" : "creator"}
                onChange={(_, val) => {
                  if (val) setIsVendor(val === "contractor");
                }}
                sx={{ mb: 2 }}
              >
                <ToggleButton value="creator">Creator</ToggleButton>
                <ToggleButton value="contractor">Contractor</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}

          <PlatformsTab
            tabValue={tabValue}
            index="platforms"
            showPlatformsTip={showPlatformsTip}
            platforms={platforms}
            setPlatforms={setPlatforms}
            instagramLink={instagramLink}
            setInstagramLink={setInstagramLink}
            instagramFollowerCount={instagramFollowerCount}
            setInstagramFollowerCount={setInstagramFollowerCount}
            instagramBrandRate={instagramBrandRate}
            setInstagramBrandRate={setInstagramBrandRate}
            instagramSongRate={instagramSongRate}
            setInstagramSongRate={setInstagramSongRate}
            instagramFeedRate={instagramFeedRate}
            setInstagramFeedRate={setInstagramFeedRate}
            instagramStoryRate={instagramStoryRate}
            setInstagramStoryRate={setInstagramStoryRate}
            tikTokLink={tikTokLink}
            setTikTokLink={setTikTokLink}
            tikTokFollowerCount={tikTokFollowerCount}
            setTikTokFollowerCount={setTikTokFollowerCount}
            tikTokBrandRate={tikTokBrandRate}
            setTikTokBrandRate={setTikTokBrandRate}
            tikTokSongRate={tikTokSongRate}
            setTikTokSongRate={setTikTokSongRate}
            youtubeLink={youtubeLink}
            setYoutubeLink={setYoutubeLink}
            youtubeFollowerCount={youtubeFollowerCount}
            setYoutubeFollowerCount={setYoutubeFollowerCount}
            youtubeIntegrationRate={youtubeIntegrationRate}
            setYoutubeIntegrationRate={setYoutubeIntegrationRate}
            youtubeShortsRate={youtubeShortsRate}
            setYoutubeShortsRate={setYoutubeShortsRate}
            youtubeDedicatedRate={youtubeDedicatedRate}
            setYoutubeDedicatedRate={setYoutubeDedicatedRate}
            xLink={xLink}
            setXLink={setXLink}
            xFollowerCount={xFollowerCount}
            setXFollowerCount={setXFollowerCount}
            xFeedPost={xFeedPost}
            setXFeedPost={setXFeedPost}
            xRepost={xRepost}
            setXRepost={setXRepost}
            podcastLink={podcastLink}
            setPodcastLink={setPodcastLink}
            podcastFollowerCount={podcastFollowerCount}
            setPodcastFollowerCount={setPodcastFollowerCount}
            podcastPreroll={podcastPreroll}
            setPodcastPreroll={setPodcastPreroll}
            podcastMidroll={podcastMidroll}
            setPodcastMidroll={setPodcastMidroll}
            podcastPostroll={podcastPostroll}
            setPodcastPostroll={setPodcastPostroll}
            podcastFull={podcastFull}
            setPodcastFull={setPodcastFull}
            kickLink={kickLink}
            setKickLink={setKickLink}
            kickFollowerCount={kickFollowerCount}
            setKickFollowerCount={setKickFollowerCount}
            kickPlacement={kickPlacement}
            setKickPlacement={setKickPlacement}
            twitchLink={twitchLink}
            setTwitchLink={setTwitchLink}
            twitchFollowerCount={twitchFollowerCount}
            setTwitchFollowerCount={setTwitchFollowerCount}
            twitchPlacement={twitchPlacement}
            setTwitchPlacement={setTwitchPlacement}
          />

          <PartnerTab
            tabValue={tabValue}
            index="partner"
            processEmails={processEmails}
            setProcessEmails={setProcessEmails}
            forwardedAlias={forwardedAlias}
            setForwardedAlias={setForwardedAlias}
            onSave={handleSaveCreator}
          />

          <NotificationsTab
            tabValue={tabValue}
            index="notifications"
            promo={promo}
            setPromo={setPromo}
            news={news}
            setNews={setNews}
            frequency={frequency}
            setFrequency={setFrequency}
            toggleItem={toggleItem}
            toggleAllPromo={toggleAllPromo}
            toggleAllNews={toggleAllNews}
          />
        </Paper>

        {/* save button – stays visible above AppShell bottom‑nav on mobile */}
        <Button
          type="submit"
          variant="contained"
          sx={(t) => ({
            mt: 1,
            mb: 2,
            position: "fixed",
            right: { xs: t.spacing(3), md: t.spacing(1) },
            bottom: {
              xs: `calc(${MOBILE_BOTTOM_NAV_HEIGHT}px + ${t.spacing(1)})`,
              md: t.spacing(1),
            },
            width: { xs: `calc(100% - ${t.spacing(6)})`, sm: "auto" },
            maxWidth: { xs: 360, sm: "none" },
            zIndex: (theme) => theme.zIndex.modal + 3, // always above nav
          })}
        >
          Save Changes
        </Button>
      </form>

      <UploadFileDialog
        open={isUploadDialogOpen}
        onClose={(url) => {
          if (url) setProfilePhotoUrl(url);
          setIsUploadDialogOpen(false);
        }}
      />
    </>
  );
}